import MediaProcessorConnector from '../mediaProcessorConnector/mediaProcessorConnector';
import createLogger from '../../helpers/log';
import updateTrackOnStreamDefault from '../../helpers/updateTrackOnStream';
import createAudioTrackHolderDefault from '../../helpers/createAudioTrackHolder';

const logging = createLogger('Subscriber');

const createAudioConnector = (deps = {}) => {
  const updateTrackOnStream = deps.updateTrackOnStream || updateTrackOnStreamDefault;
  const createAudioTrackHolder = deps.createAudioTrackHolder || createAudioTrackHolderDefault;
  let _audioMediaProcessorConnector;
  let _stream;
  let _originalAudioTrack;
  let _audioTrackHolder;

  const clearAudioHolder = () => {
    if (_audioTrackHolder) {
      _audioTrackHolder.srcObject = null;
      _audioTrackHolder = null;
    }
    _stream = null;
    _originalAudioTrack = null;
  };

  const clearConnectorAndAudioHolder = () => {
    _audioMediaProcessorConnector?.destroy();
    _audioMediaProcessorConnector = null;

    clearAudioHolder();
  };

  const applyAudioConnector = async (stream) => {
    const originalAudioTrack =
      stream === _stream ? _originalAudioTrack : stream?.getAudioTracks()[0];
    if (!originalAudioTrack) {
      const message = 'Connector not set as no audio track is present.';
      const error = new Error(message);
      logging.error(`setAudioMediaProcessorConnector: ${message}`);
      clearConnectorAndAudioHolder();
      throw error;
    }

    try {
      // We create a video element to hold a new stream containing the original audio track.
      // If the track is not used in a stream, it is stopped and no audio is heard through
      // the filtered track.
      if (_audioTrackHolder) {
        _audioTrackHolder.srcObject = null;
      }
      _audioTrackHolder = createAudioTrackHolder(originalAudioTrack);
      if (!_audioMediaProcessorConnector) {
        const message = 'Audio Media Processor Connector has not been initialized.';
        const error = new Error(message);
        logging.error(`setAudioMediaProcessorConnector: ${message}`);
        throw error;
      }
      const filteredAudioTrack = await _audioMediaProcessorConnector.setTrack(originalAudioTrack);
      _stream = stream;
      _originalAudioTrack = originalAudioTrack;
      updateTrackOnStream(
        stream,
        originalAudioTrack,
        filteredAudioTrack
      );
    } catch (err) {
      clearConnectorAndAudioHolder();
      throw err;
    }
  };

  const resetAudioStream = () => {
    try {
      const audioTracks = _stream?.getAudioTracks();
      if (audioTracks?.length) {
        const [filteredAudioTrack] = audioTracks;
        updateTrackOnStream(_stream, filteredAudioTrack, _originalAudioTrack);
      }
      clearAudioHolder();
    } catch (error) {
      throw Error(
        `setAudioMediaProcessorConnector: Error resetting track: ${error.message}`
      );
    }
  };

  return {
    get audioMediaProcessorConnector() {
      return _audioMediaProcessorConnector;
    },

    initializeConnector(mediaProcessorConnector) {
      _audioMediaProcessorConnector?.destroy();
      _audioMediaProcessorConnector = new MediaProcessorConnector(mediaProcessorConnector);
    },

    async setAudioMediaProcessorConnector(stream) {
      await applyAudioConnector(stream);
    },

    async resetAndApplyAudioConnector(stream) {
      const hadAudioMediaProcessorConnector = !!_audioMediaProcessorConnector;

      this.resetAudioStreamAndTrack();

      if (!hadAudioMediaProcessorConnector) {
        return;
      }

      await applyAudioConnector(stream);
    },

    clearAudioMediaProcessorConnector() {
      this.resetAudioStreamAndTrack();
      clearConnectorAndAudioHolder();
    },

    resetAudioStreamAndTrack() {
      if (!_originalAudioTrack) {
        return;
      }

      resetAudioStream();
    },
  };
};

export default createAudioConnector;
